import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, Copy, Download, History, ListChecks, Loader2, PauseCircle, Receipt, Replace, RotateCcw, SkipForward, Upload, Wallet, X, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Spinner } from "./common";
import { ImportConflictsPanel } from "./ImportConflictsPanel";
import { ImportReviewScreen, REVIEW_TONE, groupNeedsDecision, pendingReviewPins } from "./ImportReviewScreen";
import { PaymentDuplicatesPanel } from "./PaymentDuplicatesPanel";
import { PaymentImportBatchHistory } from "./PaymentImportBatchHistory";
import { UploadPreviewTable } from "./UploadPreviewTable";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { api, downloadBlob, errMsg, fmtDate, fmtDateTime, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

/**
 * Phase 103 — Import past payment (collection) records from Excel/CSV, matched by PIN.
 * `scope` = "main" (whole register) or "third_party" (third-party entries only). Same engine both ways.
 * Steps: 0 pick file → 1 preview & select rows → 2 applying → 3 result.
 * Sub-views: "New import" (the wizard) | "Batch history" (every applied batch + imported / failed exports).
 * Phase 138 — conflicts: rows matching an existing payment (same receipt / other amount, or same date + amount / other
 * receipt) are reviewed side by side and REPLACE the existing entry once "Replace N conflicting entries" is ticked;
 * same receipt + amount = "Already recorded – skipped".
 * Phase 194 — import REVIEW (step 1.5): a PIN that already has MANUAL (app-entered) receipts pauses the import. The
 * admin decides per PIN — Replace (soft-delete the manual receipts, import everything) or Skip (keep them, rows go to
 * Flagged Payments as "Manually reviewed — skipped"). Confirm is refused until every such PIN has a decision.
 */
const TEMPLATE_COLUMNS = ["PIN", "DATE", "RECEIPT", "CHQ NO", "NET AMT RECEIVED", "PMT MODE"];
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

export function PaymentImportSection({ code, scope = "main", readOnly = false }) {
  const tid = scope === "third_party" ? "tp-payment-import" : "payment-import";
  // Phase 152: "View only" -> no new imports; batch history stays readable (no undo / fix & re-import)
  const [view, setView] = useState(readOnly ? "history" : "import");            // import | history
  useEffect(() => { if (readOnly && view === "import") setView("history"); }, [readOnly, view]);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);         // {import_id, filename, summary, rows, review_groups}
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);   // Phase 138: "Replace N conflicting entries" ticked
  const [reviewDecisions, setReviewDecisions] = useState({});        // Phase 194: {PIN: "replace" | "skip"}
  const [reviewOpen, setReviewOpen] = useState(false);               // Phase 194: step 1.5 (review screen) is showing
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(null);      // "imported" | "failed" while the result card downloads
  const [advBusy, setAdvBusy] = useState(null);          // Phase 150: history row whose advance-credit Excel is downloading
  const [job, setJob] = useState(null);                  // Phase 108: background progress {status, processed, total, imported, failed, amount}
  const fileInputRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.get(`/t/${code}/payment-import/history`, { params: { scope, limit: 10 } })
      .then((r) => setHistory(r.data || [])).catch(() => {});
  }, [code, scope]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Phase 150: Excel of the payments in one batch that exceeded the demand (PIN, owner, demand, payment, advance credit)
  const exportAdvance = async (h) => {
    setAdvBusy(h.id);
    try {
      await downloadBlob(`/t/${code}/payment-import/${h.id}/export?kind=advance`, `${(h.filename || "payment-import").replace(/\.[^.]+$/, "")}_advance.xlsx`);
      toast.success("Advance credit records exported", { description: `${fmtINR(h.summary?.advance || 0)} advance · ${h.filename}` });
    } catch (err) { toast.error(errMsg(err)); } finally { setAdvBusy(null); }
  };

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/payment-import/template?scope=${scope}`,
        scope === "third_party" ? "third-party-payment-import-template.xlsx" : "payment-import-template.xlsx");
      toast.success("Template downloaded — one row per receipt");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const runPreview = async () => {
    if (!file) return toast.error("Choose the filled Import Payments file first");
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", scope);
      const r = await api.post(`/t/${code}/payment-import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setPreview(d);
      setSelectedRows(new Set((d.rows || []).filter((x) => x.status !== "error").map((x) => x.row_number)));
      setReplaceConfirmed(false);
      setReviewDecisions({});
      setReviewOpen(false);
      setStep(1);
      const s = d.summary || {};
      if (d.review_required || s.review_pins > 0) toast.warning(`Import paused — manual receipts exist on ${s.review_pins} PIN${s.review_pins === 1 ? "" : "s"}`, {
        description: `Choose Replace or Skip for each PIN in the import review before confirming${s.conflict_rows > 0 ? ` · ${s.conflict_rows} other row(s) conflict with existing entries` : ""}`,
      });
      else if (s.conflict_rows > 0) toast.warning(`${s.conflict_rows} row(s) conflict with existing entries — review before confirming`, {
        description: `${s.valid_rows} row(s) ready${s.already_recorded_rows > 0 ? ` · ${s.already_recorded_rows} already recorded (skipped)` : ""}`,
      });
      else if (s.error_rows > 0) toast.warning(`${s.valid_rows} row(s) ready, ${s.error_rows} locked — review the preview`);
      else toast.success(`${s.valid_rows} payment(s) ready to import — review and confirm`);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const applySelected = async () => {
    if (!preview?.import_id) return;
    if (selectedRows.size === 0) return toast.error("Select at least one row to import");
    const replacing = (preview.rows || []).filter((r) => r.action === "replace" && r.status !== "error" && selectedRows.has(r.row_number)).length;
    if (replacing > 0 && !replaceConfirmed) return toast.error(`Tick "Replace ${replacing} conflicting entr${replacing === 1 ? "y" : "ies"}" to confirm, or untick those rows`);
    // Phase 194: every review PIN with a ticked row must have Replace / Skip — otherwise open the review screen
    const groups = preview.review_groups || [];
    const pending = pendingReviewPins(groups, selectedRows, reviewDecisions);
    if (pending.length > 0) {
      setReviewOpen(true);
      return toast.warning(`Import paused — choose Replace or Skip for ${pending.length} PIN${pending.length === 1 ? "" : "s"} first`);
    }
    const decisions = {};
    groups.forEach((g) => { if (groupNeedsDecision(g, selectedRows) && reviewDecisions[g.pin]) decisions[g.pin] = reviewDecisions[g.pin]; });
    const nReplace = Object.values(decisions).filter((v) => v === "replace").length;
    const nSkip = Object.values(decisions).filter((v) => v === "skip").length;
    setBusy(true);
    setStep(2);
    setError(null);
    try {
      const r = await api.post(`/t/${code}/payment-import/${preview.import_id}/apply`,
        { selected_rows: [...selectedRows], replace_conflicts: replacing > 0 && replaceConfirmed, review_decisions: decisions });
      // Phase 108: the server answers at once and writes the rows in the background (chunks of ~250)
      setResult(null);
      setReviewOpen(false);
      setJob({ status: "processing", processed: 0, total: r.data.selected_rows ?? selectedRows.size, imported: 0, failed: 0, amount: 0 });
      setStep(3);
      toast.success("Import started — it is being processed in the background", {
        description: `${(r.data.selected_rows ?? selectedRows.size).toLocaleString("en-IN")} row(s)${replacing > 0 ? ` · ${replacing} replacing existing entries` : ""}`
          + `${nReplace + nSkip > 0 ? ` · import review: ${nReplace} PIN(s) replace manual receipts, ${nSkip} skipped` : ""} · track progress here or in Batch history`,
      });
    } catch (err) {
      const msg = errMsg(err);
      setError(msg);
      setStep(1);
      // the server still sees the review as incomplete (e.g. the manual receipts changed) → back to the review screen
      if (err?.response?.status === 409 && /paused|review/i.test(msg)) setReviewOpen(true);
    } finally { setBusy(false); }
  };

  // Phase 108: follow the background job until it finishes, then show the final result card
  useEffect(() => {
    if (step !== 3 || !preview?.import_id || !job || job.status !== "processing") return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/t/${code}/payment-import/${preview.import_id}`, { timeout: 15000 });
        if (stopped) return;
        const d = r.data;
        const p = d.progress || {};
        if (d.status === "completed") {
          pokeLiveCollections();   // Phase 149
          setJob({ ...p, status: "completed" });
          setResult(d.summary);
          const s = d.summary || {};
          if (s.failed_rows > 0) toast.warning(`${s.imported_rows} payment(s) imported, ${s.failed_rows} failed`);
          else toast.success(`${s.imported_rows} payment(s) imported — ${fmtINR(s.amount)} recorded`);
          loadHistory();
          return;
        }
        if (d.status === "failed") { setJob({ ...p, status: "failed" }); return; }
        setJob({ ...p, status: "processing" });
      } catch { /* transient — keep polling */ }
      if (!stopped) timer = setTimeout(poll, 1500);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [step, code, preview?.import_id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setStep(0); setFile(null); setPreview(null); setSelectedRows(new Set()); setReplaceConfirmed(false); setResult(null); setError(null); setJob(null);
    setReviewDecisions({}); setReviewOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const exportResult = async (kind) => {
    if (!preview?.import_id) return;
    setExporting(kind);
    try {
      await downloadBlob(`/t/${code}/payment-import/${preview.import_id}/export?kind=${kind}`, `${preview.filename || "payment-import"}_${kind}.xlsx`);
      toast.success(kind === "imported" ? "Imported records exported" : kind === "advance" ? "Advance credit records exported"
        : "Failed records exported — fix and upload the file again");
    } catch (err) { toast.error(errMsg(err)); } finally { setExporting(null); }
  };

  const rows = preview?.rows || [];
  const summary = preview?.summary || {};
  const processable = rows.filter((r) => r.status !== "error").length;
  const picked = rows.filter((r) => selectedRows.has(r.row_number));
  const pickedAmount = picked.reduce((n, r) => n + (r.amount || 0), 0);
  const pickedAlloc = picked.reduce((n, r) => n + (r.allocations || []).reduce((m, a) => m + (a.amount || 0), 0), 0);
  const pickedAdvance = picked.reduce((n, r) => n + (r.advance_amount || 0), 0);
  const pickedProps = new Set(picked.map((r) => r.property_id)).size;
  const pickedReplacing = picked.filter((r) => r.action === "replace").length;          // Phase 138
  const conflictRows = rows.filter((r) => r.action === "replace" && r.status !== "error").length;
  const needsReplaceConfirm = pickedReplacing > 0 && !replaceConfirmed;
  const resultFailed = result ? (result.error_rows || 0) + (result.failed_rows || 0) + (result.deselected_rows || 0) + (result.review_skipped_rows || 0) : 0;
  // Phase 194: import review — PINs with manual receipts (only those with a ticked row need a decision)
  const reviewGroups = preview?.review_groups || [];
  const reviewNeeded = reviewGroups.filter((g) => groupNeedsDecision(g, selectedRows));
  const reviewPending = pendingReviewPins(reviewGroups, selectedRows, reviewDecisions);
  const reviewReplacePins = reviewNeeded.filter((g) => reviewDecisions[g.pin] === "replace").length;
  const reviewSkipPins = reviewNeeded.filter((g) => reviewDecisions[g.pin] === "skip").length;
  const reviewSkipRows = picked.filter((r) => r.action === "review" && reviewDecisions[r.pin] === "skip").length;
  const reviewRows = rows.filter((r) => r.action === "review" && r.status !== "error").length;
  const importCount = selectedRows.size - reviewSkipRows;
  const toggleRow = (n) => {
    const next = new Set(selectedRows);
    if (next.has(n)) next.delete(n); else next.add(n);
    setSelectedRows(next);
  };

  // Phase 107: Batch history → "Fix & re-import" hands a validated preview to this wizard (same tick → confirm flow)
  const startRetryPreview = (d) => {
    setPreview(d);
    setSelectedRows(new Set((d.rows || []).filter((x) => x.status !== "error").map((x) => x.row_number)));
    setReplaceConfirmed(false);
    setReviewDecisions({}); setReviewOpen(false);
    setResult(null); setError(null); setFile(null);
    setView("import");
    setStep(1);
  };

  const flaggedPath = `/${code}/admin/flagged-payments`;

  return (
    <div className="space-y-4">
      {step === 0 && (
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid={`${tid}-view-tabs`}>
            {!readOnly && <TabsTrigger value="import" className="gap-1.5" data-testid={`${tid}-view-import`}><Upload className="h-3.5 w-3.5" /> New import</TabsTrigger>}
            <TabsTrigger value="history" className="gap-1.5" data-testid={`${tid}-view-history`}><History className="h-3.5 w-3.5" /> Batch history</TabsTrigger>
            {scope === "main" && (
              <TabsTrigger value="duplicates" className="gap-1.5" data-testid={`${tid}-view-duplicates`}><Copy className="h-3.5 w-3.5" /> Consistency</TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      )}

      {step === 0 && view === "history" && <PaymentImportBatchHistory code={code} scope={scope} onRetryPreview={readOnly ? null : startRetryPreview} readOnly={readOnly} />}
      {step === 0 && view === "duplicates" && scope === "main" && <PaymentDuplicatesPanel code={code} />}

      {step === 0 && view === "import" && !readOnly && (
        <Card className="shadow-sm max-w-3xl" data-testid={`${tid}-card`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Import past payment records{scope === "third_party" ? " — third-party entries" : ""}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Rows are matched to {scope === "third_party" ? "third-party properties" : "properties"} by <b>PIN</b> and recorded as new collections
              dated as in the file. A row whose <b>receipt no. and amount already exist</b> for the PIN is skipped as already recorded.
              A row that <b>conflicts</b> with an existing entry — same receipt no. with a different amount, or same date and amount with a
              different receipt no. — is shown side by side in the preview and <b>replaces</b> the existing entry once you confirm; the
              bulk-imported record is the final one. A PIN that already has <b>manual receipts entered in the app by staff</b> pauses the
              import for an <b>import review</b> — you choose per PIN whether the import <b>replaces</b> those receipts or is <b>skipped</b> (sent to
              Flagged Payments). Money is applied to the oldest outstanding demand first; anything left over becomes advance credit.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">File columns</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`${tid}-columns`}>
                {TEMPLATE_COLUMNS.map((c) => (
                  <Badge key={c} variant={c === "CHQ NO" ? "outline" : "secondary"} className="text-[10px] font-normal">{c}{c === "CHQ NO" ? " (optional)" : ""}</Badge>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                DATE accepts DD-MM-YYYY, DD/MM/YYYY or YYYY-MM-DD. PMT MODE: Cash, Cheque, DD, UPI, Online / NEFT / RTGS, Card, PoS.
                Each imported payment is <b>credited to the property's assigned Tax Collector</b> (or its Supervisor when no collector is mapped) and
                counts towards their Daily Field Activity, Monthly Performance, Leaderboard, targets and Visit Reports exactly like an app-entered
                payment. Unassigned properties are recorded as <b>Imported (Legacy)</b> without staff credit.
              </p>
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={downloadTemplate} data-testid={`${tid}-template-button`}>
              <Download className="h-4 w-4 mr-2" /> Download import template
            </Button>
            <div className="space-y-1">
              <Label htmlFor={`${tid}-file`}>Payment records file (.xlsx / .csv)</Label>
              <Input id={`${tid}-file`} ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" disabled={busy}
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`${tid}-file-input`} />
              <p className="text-xs text-muted-foreground">Up to 5,000 rows per file. Nothing is recorded until you confirm in the preview.</p>
            </div>
            {error && (
              <Alert variant="destructive" data-testid={`${tid}-error`}>
                <AlertTitle>Could not read the file</AlertTitle>
                <AlertDescription data-testid={`${tid}-error-message`}>{error}</AlertDescription>
              </Alert>
            )}
            <Button className="w-full" onClick={runPreview} disabled={busy || !file} data-testid={`${tid}-preview-button`}>
              <Upload className="h-4 w-4 mr-2" /> {busy ? "Reading file & matching PINs…" : "Upload & preview"}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 1 && preview && reviewOpen && (
        <ImportReviewScreen groups={reviewGroups} decisions={reviewDecisions} onChange={setReviewDecisions} selected={selectedRows}
          onProceed={applySelected} onBack={() => setReviewOpen(false)} busy={busy} tid={tid} filename={preview.filename} error={error} />
      )}

      {step === 1 && preview && !reviewOpen && (
        <Card className="shadow-sm" data-testid={`${tid}-preview-card`}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Receipt className="h-4 w-4" /> Preview &amp; select payments
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid={`${tid}-filename`}>
                  {preview.filename} · {summary.total_rows} row{summary.total_rows === 1 ? "" : "s"}. Filter by zone, ward or mohalla and untick anything that should not be imported.
                  Locked rows (duplicates, unknown PINs, bad values) are always skipped.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={reset} disabled={busy} data-testid={`${tid}-change-file`}>
                <X className="h-4 w-4 mr-1" /> Choose another file
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {reviewGroups.length > 0 && (   /* Phase 194: the import is paused until every review PIN has a decision */
              <Alert className={`${reviewPending.length > 0 ? "border-indigo-300 bg-indigo-50 text-indigo-950" : "border-emerald-300 bg-emerald-50 text-emerald-950"} [&>svg]:text-current`} data-testid={`${tid}-review-banner`}>
                {reviewPending.length > 0 ? <PauseCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                <AlertTitle data-testid={`${tid}-review-banner-title`}>
                  {reviewPending.length > 0
                    ? `Import paused — manual receipts exist on ${reviewNeeded.length} ${plural(reviewNeeded.length, "PIN")}`
                    : reviewNeeded.length > 0
                      ? `Import review complete — ${reviewReplacePins} ${plural(reviewReplacePins, "PIN")} replace manual receipts · ${reviewSkipPins} skipped`
                      : "Import review not needed — no ticked rows on the PINs that have manual receipts"}
                </AlertTitle>
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs" data-testid={`${tid}-review-banner-text`}>
                    {reviewPending.length > 0
                      ? <>{reviewNeeded.length - reviewPending.length} of {reviewNeeded.length} decided. Rows marked <i>Needs review</i> are not imported until you choose <b>Replace</b> (remove the manual receipts) or <b>Skip</b> (keep them, rows go to Flagged Payments) for every PIN.</>
                      : <>{reviewSkipRows > 0 ? `${reviewSkipRows} skipped ${plural(reviewSkipRows, "row")} will be listed under Reports → Flagged Payments. ` : ""}You can still change the decisions before confirming.</>}
                  </span>
                  {reviewNeeded.length > 0 && (
                    <Button size="sm" variant={reviewPending.length > 0 ? "default" : "outline"} className="h-8" onClick={() => { setError(null); setReviewOpen(true); }} disabled={busy} data-testid={`${tid}-open-review`}>
                      <ListChecks className="h-3.5 w-3.5 mr-1.5" /> {reviewPending.length > 0 ? `Review ${reviewNeeded.length} ${plural(reviewNeeded.length, "PIN")}` : "Change decisions"}
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className={`grid grid-cols-2 sm:grid-cols-3 ${reviewGroups.length > 0 ? "lg:grid-cols-7" : "lg:grid-cols-6"} gap-3`}>
              <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3 text-center">
                <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
                <p className="text-xl font-semibold text-emerald-700 tabular-nums" data-testid={`${tid}-valid-rows`}>{summary.valid_rows ?? 0}</p>
                <p className="text-[11px] text-emerald-700">Payments ready</p>
              </div>
              <div className="rounded-xl border bg-amber-50 border-amber-200 p-3 text-center">
                <AlertTriangle className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                <p className="text-xl font-semibold text-amber-700 tabular-nums" data-testid={`${tid}-warning-rows`}>{summary.warning_rows ?? 0}</p>
                <p className="text-[11px] text-amber-700">With warnings</p>
              </div>
              {reviewGroups.length > 0 && (
                <div className={`rounded-xl border p-3 text-center ${REVIEW_TONE}`} data-testid={`${tid}-review-card-tile`}>
                  <PauseCircle className="h-4 w-4 mx-auto mb-1" />
                  <p className="text-xl font-semibold tabular-nums" data-testid={`${tid}-review-rows`}>{reviewRows}</p>
                  <p className="text-[11px]">Needs review · {summary.review_pins ?? reviewGroups.length} {plural(summary.review_pins ?? reviewGroups.length, "PIN")}</p>
                </div>
              )}
              <div className={`rounded-xl border p-3 text-center ${conflictRows > 0 ? "bg-amber-100/70 border-amber-400" : "bg-muted/40"}`} data-testid={`${tid}-conflict-card`}>
                <Replace className={`h-4 w-4 mx-auto mb-1 ${conflictRows > 0 ? "text-amber-800" : "text-muted-foreground"}`} />
                <p className={`text-xl font-semibold tabular-nums ${conflictRows > 0 ? "text-amber-900" : "text-muted-foreground"}`} data-testid={`${tid}-conflict-rows`}>{summary.conflict_rows ?? 0}</p>
                <p className={`text-[11px] ${conflictRows > 0 ? "text-amber-900" : "text-muted-foreground"}`}>Conflicts · will replace</p>
              </div>
              <div className="rounded-xl border bg-red-50 border-red-200 p-3 text-center">
                <XCircle className="h-4 w-4 mx-auto text-red-600 mb-1" />
                <p className="text-xl font-semibold text-red-700 tabular-nums" data-testid={`${tid}-error-rows`}>{summary.error_rows ?? 0}</p>
                <p className="text-[11px] text-red-700" data-testid={`${tid}-already-recorded-rows`}>
                  Locked{summary.already_recorded_rows > 0 ? ` · ${summary.already_recorded_rows} already recorded` : ""}
                </p>
              </div>
              <div className="rounded-xl border bg-sky-50 border-sky-200 p-3 text-center">
                <Receipt className="h-4 w-4 mx-auto text-sky-600 mb-1" />
                <p className="text-xl font-semibold text-sky-700 tabular-nums" data-testid={`${tid}-amount`}>{fmtINR(summary.amount ?? 0)}</p>
                <p className="text-[11px] text-sky-700">Total in file (ready rows)</p>
              </div>
              <div className="rounded-xl border bg-muted/40 p-3 text-center">
                <Wallet className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                <p className="text-xl font-semibold tabular-nums" data-testid={`${tid}-advance`}>{fmtINR(summary.advance ?? 0)}</p>
                <p className="text-[11px] text-muted-foreground">Advance credit · {summary.demands_touched ?? 0} demand{summary.demands_touched === 1 ? "" : "s"}</p>
              </div>
            </div>

            <UploadPreviewTable rows={rows} selected={selectedRows} onChange={setSelectedRows} variant="payment-import" />

            <ImportConflictsPanel rows={rows} selected={selectedRows} onToggle={toggleRow} tid={tid} />

            {error && (
              <Alert variant="destructive" data-testid={`${tid}-apply-error`}>
                <AlertTitle>Import could not be applied</AlertTitle>
                <AlertDescription data-testid={`${tid}-apply-error-message`}>{error}</AlertDescription>
              </Alert>
            )}

            <div className="rounded-lg border bg-muted/30" data-testid={`${tid}-confirm-bar`}>
              {pickedReplacing > 0 && (
                <label className={`flex items-start gap-3 px-4 py-3 border-b cursor-pointer ${replaceConfirmed ? "bg-amber-50/60" : "bg-amber-100/60"}`}
                  htmlFor={`${tid}-replace-confirm`} data-testid={`${tid}-replace-confirm-row`}>
                  <Checkbox id={`${tid}-replace-confirm`} checked={replaceConfirmed} onCheckedChange={(v) => setReplaceConfirmed(v === true)}
                    className="mt-0.5" data-testid={`${tid}-replace-confirm`} />
                  <span className="text-sm">
                    <b className="text-amber-900" data-testid={`${tid}-replace-confirm-label`}>Replace {pickedReplacing} conflicting entr{pickedReplacing === 1 ? "y" : "ies"}</b>
                    <span className="text-amber-900/90"> with the imported data.</span>
                    <span className="block text-xs text-amber-900/80 mt-0.5">
                      The existing record{pickedReplacing === 1 ? "" : "s"} stay{pickedReplacing === 1 ? "s" : ""} in the ledger as <i>Replaced by import</i> (audit only, excluded from all totals and reports);
                      the imported payment{pickedReplacing === 1 ? "" : "s"} become{pickedReplacing === 1 ? "s" : ""} the final record{pickedReplacing === 1 ? "" : "s"}, credited to the same staff. Rolling back the batch restores the replaced entr{pickedReplacing === 1 ? "y" : "ies"}.
                    </span>
                  </span>
                </label>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="text-sm" data-testid={`${tid}-selection-summary`}>
                  <b className="tabular-nums">{selectedRows.size}</b> of {processable} processable rows selected
                  <span className="text-xs text-muted-foreground"> · {fmtINR(pickedAmount)} across {pickedProps} propert{pickedProps === 1 ? "y" : "ies"}
                    {" · "}{fmtINR(pickedAlloc)} against demands
                    {pickedAdvance > 0 ? ` · ${fmtINR(pickedAdvance)} advance` : ""}
                    {pickedReplacing > 0 ? ` · ${pickedReplacing} replace existing` : ""}
                    {reviewNeeded.length > 0 ? ` · review: ${reviewReplacePins} replace / ${reviewSkipPins} skip of ${reviewNeeded.length} ${plural(reviewNeeded.length, "PIN")}` : ""}
                    {reviewSkipRows > 0 ? ` · ${reviewSkipRows} skipped to Flagged Payments` : ""}
                    {processable - selectedRows.size > 0 ? ` · ${processable - selectedRows.size} deselected` : ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={reset} disabled={busy} data-testid={`${tid}-cancel`}>Cancel</Button>
                  {reviewPending.length > 0 ? (
                    <Button onClick={() => { setError(null); setReviewOpen(true); }} disabled={busy || selectedRows.size === 0} data-testid={`${tid}-apply-button`}
                      title={`Import paused — choose Replace or Skip for ${reviewPending.length} ${plural(reviewPending.length, "PIN")} with manual receipts first`}>
                      <ListChecks className="h-4 w-4 mr-1.5" /> Review {reviewPending.length} {plural(reviewPending.length, "PIN")} to continue <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  ) : (
                    <Button onClick={applySelected} disabled={busy || selectedRows.size === 0 || needsReplaceConfirm} data-testid={`${tid}-apply-button`}
                      title={needsReplaceConfirm ? "Tick the replacement confirmation first, or untick the conflicting rows" : undefined}>
                      Confirm — import {importCount} payment{importCount === 1 ? "" : "s"}{pickedReplacing > 0 ? ` (${pickedReplacing} replace existing)` : ""}
                      {reviewNeeded.length > 0 ? ` · ${reviewReplacePins} replace / ${reviewSkipPins} skip` : ""}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && <Spinner label="Starting the import…" />}

      {step === 3 && !result && job && (
        <Card className="shadow-sm" data-testid={`${tid}-processing`}>
          <CardContent className="p-8 text-center">
            {job.status === "failed" ? (
              <XCircle className="h-12 w-12 mx-auto text-destructive mb-3" />
            ) : (
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
              </span>
            )}
            <h2 className="text-xl font-semibold font-heading" data-testid={`${tid}-processing-title`}>
              {job.status === "failed" ? "Import stopped part-way" : "Import started — processing in the background"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1" data-testid={`${tid}-processing-subtitle`}>
              {preview?.filename} · {(job.total || 0).toLocaleString("en-IN")} row{job.total === 1 ? "" : "s"} are being recorded in batches of {job.chunk_size || 250}. You can leave this page — progress and the final result are in Batch history.
            </p>
            <div className="max-w-md mx-auto mt-6 space-y-2" role="status" aria-live="polite" data-testid={`${tid}-processing-progress`}>
              <Progress value={job.total ? Math.min(100, Math.round(((job.processed || 0) / job.total) * 100)) : 0} />
              <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                <span data-testid={`${tid}-processing-count`}>{(job.processed || 0).toLocaleString("en-IN")} / {(job.total || 0).toLocaleString("en-IN")} rows</span>
                <span><span className="text-emerald-700 font-medium">{job.imported || 0} imported</span>{job.failed > 0 && <> · <span className="text-destructive font-medium">{job.failed} failed</span></>} · {fmtINR(job.amount || 0)}</span>
              </div>
            </div>
            {job.status === "failed" && (
              <Alert variant="destructive" className="max-w-md mx-auto mt-4 text-left" data-testid={`${tid}-processing-error`}>
                <AlertTitle>Stopped: {job.error || "unexpected error"}</AlertTitle>
                <AlertDescription>Rows already written are kept. Use <b>Resume</b> in Batch history to continue without duplicates.</AlertDescription>
              </Alert>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <Button variant="outline" onClick={() => { reset(); setView("history"); }} data-testid={`${tid}-processing-history-button`}>
                <History className="h-4 w-4 mr-2" /> Track in Batch history
              </Button>
              <Button onClick={reset} data-testid={`${tid}-another-button`}>
                <RotateCcw className="h-4 w-4 mr-2" /> Import another file
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && result && (
        <Card className="shadow-sm" data-testid={`${tid}-result`}>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <h2 className="text-xl font-semibold font-heading" data-testid={`${tid}-result-title`}>Payments imported</h2>
            <p className="text-sm text-muted-foreground mt-1">{preview?.filename} · {result.total_rows} row{result.total_rows === 1 ? "" : "s"} in file</p>
            <div className={`grid grid-cols-2 ${result.replaced_rows > 0 ? "sm:grid-cols-5" : "sm:grid-cols-4"} gap-3 mt-6 max-w-3xl mx-auto`}>
              {[
                ["Payments recorded", result.imported_rows, `${tid}-result-imported`, "text-emerald-700"],
                ...(result.replaced_rows > 0 ? [["Existing entries replaced", result.replaced_rows, `${tid}-result-replaced`, "text-amber-800"]] : []),
                ["Amount recorded", fmtINR(result.amount), `${tid}-result-amount`, ""],
                ["Applied to demands", fmtINR(result.allocated), `${tid}-result-allocated`, ""],
                ["Advance credit", fmtINR(result.advance), `${tid}-result-advance`, result.advance > 0 ? "text-amber-700" : ""],
              ].map(([l, v, t, cls]) => (
                <div key={l} className="rounded-xl border p-4">
                  <p className={`text-2xl font-semibold tabular-nums ${cls}`} data-testid={t}>{v ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
            {(result.review_replaced_pins > 0 || result.review_skipped_pins > 0) && (   /* Phase 194: import review outcome */
              <div className="mt-4 max-w-3xl mx-auto rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 text-left" data-testid={`${tid}-result-review`}>
                <p className="text-sm font-semibold text-indigo-950 flex items-center gap-2"><ListChecks className="h-4 w-4" aria-hidden="true" /> Import review — {result.review_pins} {plural(result.review_pins, "PIN")} with manual receipts</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  {[
                    ["PINs replaced", result.review_replaced_pins, `${tid}-result-review-replaced-pins`, "text-amber-800"],
                    ["Manual receipts removed", `${result.review_replaced_receipts || 0} · ${fmtINR(result.review_replaced_amount)}`, `${tid}-result-review-replaced-receipts`, "text-amber-800"],
                    ["PINs skipped", result.review_skipped_pins, `${tid}-result-review-skipped-pins`, "text-sky-800"],
                    ["Rows sent to Flagged Payments", `${result.review_skipped_rows || 0} · ${fmtINR(result.review_skipped_amount)}`, `${tid}-result-review-skipped-rows`, "text-sky-800"],
                  ].map(([l, v, t, cls]) => (
                    <div key={l} className="rounded-lg border bg-background p-3">
                      <p className={`text-lg font-semibold tabular-nums ${cls}`} data-testid={t}>{v ?? 0}</p>
                      <p className="text-[11px] text-muted-foreground">{l}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-indigo-950/80 mt-3" data-testid={`${tid}-result-review-note`}>
                  {result.review_replaced_receipts > 0 && <><Replace className="h-3.5 w-3.5 inline mr-1 -mt-0.5" aria-hidden="true" />{result.review_replaced_receipts} manual {plural(result.review_replaced_receipts, "receipt")} {plural(result.review_replaced_receipts, "was", "were")} moved to Deleted receipts as <i>Replaced by bulk import</i> (restorable for 30 days; each PIN is logged in the Audit Trail with your name, the time and the receipt numbers). </>}
                  {result.review_skipped_rows > 0 && <><SkipForward className="h-3.5 w-3.5 inline mr-1 -mt-0.5" aria-hidden="true" />{result.review_skipped_rows} skipped {plural(result.review_skipped_rows, "row")} {plural(result.review_skipped_rows, "was", "were")} not recorded — {plural(result.review_skipped_rows, "it is", "they are")} listed under <Link to={flaggedPath} className="font-semibold underline underline-offset-2" data-testid={`${tid}-result-review-flagged-link`}>Reports → Flagged Payments</Link> as <i>Manually reviewed — skipped</i>, where you can mark {plural(result.review_skipped_rows, "it", "them")} reviewed or use <b>Import now</b>.</>}
                </p>
              </div>
            )}
            {result.replaced_rows > 0 && (
              <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block" data-testid={`${tid}-result-replaced-note`}>
                <Replace className="h-3.5 w-3.5 inline mr-1 -mt-0.5" aria-hidden="true" />
                {result.replaced_rows} existing entr{result.replaced_rows === 1 ? "y" : "ies"} ({fmtINR(result.replaced_amount)}) {result.replaced_rows === 1 ? "was" : "were"} marked
                <i> Replaced by import</i> and no longer count{result.replaced_rows === 1 ? "s" : ""} in totals — the imported receipts are the final records.
              </p>
            )}
            {(result.flagged_rows > 0 || result.partial_rows > 0) && (   /* Phase 190: data flags written at import time */
              <p className="mt-3 text-xs text-red-900 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-block" data-testid={`${tid}-result-flags-note`}>
                {result.flagged_rows > 0 && <><b className="tabular-nums">{result.flagged_rows}</b> row{result.flagged_rows === 1 ? "" : "s"} exceeded the demand due and {result.flagged_rows === 1 ? "has" : "have"} been flagged <i>Bulk Import Excess</i> — review {result.flagged_rows === 1 ? "it" : "them"} under <b>Reports → Flagged Payments</b>. </>}
                {result.partial_rows > 0 && <><b className="tabular-nums">{result.partial_rows}</b> row{result.partial_rows === 1 ? " was" : "s were"} below the demand due and {result.partial_rows === 1 ? "is" : "are"} marked partial.</>}
              </p>
            )}
            <p className="mt-4 text-sm text-muted-foreground" data-testid={`${tid}-result-skipped`}>
              {result.properties} propert{result.properties === 1 ? "y" : "ies"} updated.
              {result.failed_rows > 0 && <> <b className="text-destructive tabular-nums">{result.failed_rows}</b> row{result.failed_rows === 1 ? "" : "s"} failed.</>}
              {result.deselected_rows > 0 && <> <b className="text-foreground tabular-nums">{result.deselected_rows}</b> row{result.deselected_rows === 1 ? " was" : "s were"} deselected and left out.</>}
              {result.already_recorded_rows > 0 && <> <b className="text-foreground tabular-nums">{result.already_recorded_rows}</b> row{result.already_recorded_rows === 1 ? " was" : "s were"} already recorded and skipped.</>}
              {result.review_skipped_rows > 0 && <> <b className="text-foreground tabular-nums">{result.review_skipped_rows}</b> row{result.review_skipped_rows === 1 ? " was" : "s were"} skipped in the import review (manual receipts kept).</>}
              {(result.error_rows || 0) - (result.already_recorded_rows || 0) > 0 && <> <b className="text-foreground tabular-nums">{(result.error_rows || 0) - (result.already_recorded_rows || 0)}</b> locked row{(result.error_rows || 0) - (result.already_recorded_rows || 0) === 1 ? " was" : "s were"} skipped.</>}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-testid={`${tid}-result-actions`}>
              <Button variant="outline" onClick={() => exportResult("imported")} disabled={!!exporting || !(result.imported_rows > 0)} data-testid={`${tid}-result-export-imported`}>
                <Download className="h-4 w-4 mr-2" /> {exporting === "imported" ? "Exporting…" : `Imported records (${result.imported_rows || 0})`}
              </Button>
              <Button variant="outline" className={resultFailed > 0 ? "border-destructive/40 text-destructive hover:bg-destructive/5" : ""}
                onClick={() => exportResult("failed")} disabled={!!exporting || resultFailed === 0} data-testid={`${tid}-result-export-failed`}>
                <Download className="h-4 w-4 mr-2" /> {exporting === "failed" ? "Exporting…" : `Failed / skipped records (${resultFailed})`}
              </Button>
              {result.advance > 0 && (
                <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => exportResult("advance")}
                  disabled={!!exporting} title="Properties where the payment exceeded the demand" data-testid={`${tid}-result-export-advance`}>
                  <Download className="h-4 w-4 mr-2" /> {exporting === "advance" ? "Exporting…" : `Advance credit records (${fmtINR(result.advance)})`}
                </Button>
              )}
              <Button onClick={reset} data-testid={`${tid}-another-button`}>
                <RotateCcw className="h-4 w-4 mr-2" /> Import another file
              </Button>
            </div>
            {resultFailed > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">The failed file keeps the template columns plus IMPORT STATUS and REASON — fix the rows and upload it again.</p>
            )}
          </CardContent>
        </Card>
      )}

      {step === 0 && view === "import" && history.length > 0 && (
        <Card className="shadow-sm max-w-3xl" data-testid={`${tid}-history`}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4" /> Recent imports</CardTitle>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setView("history")} data-testid={`${tid}-history-view-all`}>
              View batch history
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 text-xs">When</TableHead>
                  <TableHead className="h-8 text-xs">File</TableHead>
                  <TableHead className="h-8 text-xs text-right">Imported</TableHead>
                  <TableHead className="h-8 text-xs text-right">Amount</TableHead>
                  <TableHead className="h-8 text-xs text-right">Advance</TableHead>
                  <TableHead className="h-8 text-xs">By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id} data-testid={`${tid}-history-row-${h.id}`}>
                    <TableCell className="py-1.5 text-xs whitespace-nowrap">{fmtDateTime(h.applied_at)}</TableCell>
                    <TableCell className="py-1.5 text-xs truncate max-w-[200px]" title={h.filename}>{h.filename}</TableCell>
                    <TableCell className="py-1.5 text-xs text-right tabular-nums">{h.summary?.imported_rows ?? 0} / {h.total_rows}</TableCell>
                    <TableCell className="py-1.5 text-xs text-right tabular-nums">{fmtINR(h.summary?.amount ?? 0)}</TableCell>
                    <TableCell className="py-1.5 text-xs text-right tabular-nums">
                      <span className="inline-flex items-center justify-end gap-1">
                        {fmtINR(h.summary?.advance ?? 0)}
                        {(h.summary?.advance ?? 0) > 0 && (
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-amber-800 hover:text-amber-900 hover:bg-amber-50"
                            title="Download Excel of the payments that exceeded the demand (PIN, owner, demand, payment, advance credit)"
                            aria-label="Download advance credit records as Excel" disabled={advBusy === h.id}
                            onClick={() => exportAdvance(h)} data-testid={`${tid}-history-advance-export-${h.id}`}>
                            {advBusy === h.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                          </Button>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="py-1.5 text-xs">{h.applied_by_name || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PaymentImportSection;
